import { useMemo } from "react";
import { emptyRouteSummary } from "../../../shared/monitoringRouteAuth";
import type {
  MonitoringWorkflowDependenciesResponse,
  MonitoringWorkflowDependenciesRow,
  MonitoringWorkflowRoutesResponse,
  MonitoringWorkflowRoutesRow,
  MonitoringWorkflowsResponse,
} from "../../../shared/monitoringWorkflowsReport";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { useRevisionedJson } from "../../hooks/useRevisionedJson";
import type { DisplayableSpace } from "../../hooks/useSpaceDisplayName";
import {
  monitoringWorkflowDependenciesPath,
  monitoringWorkflowRoutesPath,
  monitoringWorkflowsPath,
} from "../../lib/apiPaths";

const MONITOR_STATS_WINDOW_DAYS = 30;

const EMPTY_REPORT: MonitoringWorkflowsResponse = {
  workflows: [],
  failing: [],
  failureTrend: [],
  storage: [],
  units: null,
  routeSummary: emptyRouteSummary(),
  dependencySummary: [],
};

// Panels must read through this hook (same path, same query string) so the tab
// makes one request instead of one per panel.
export function useMonitoringWorkflows(): {
  report: MonitoringWorkflowsResponse;
  loading: boolean;
} {
  const { data, loading } = useRevisionedJson<MonitoringWorkflowsResponse>(
    monitoringWorkflowsPath(),
    `days=${MONITOR_STATS_WINDOW_DAYS}`
  );
  return { report: data ?? EMPTY_REPORT, loading };
}

export function useMonitoringWorkflowRoutes(open: boolean): {
  workflows: MonitoringWorkflowRoutesRow[];
  loading: boolean;
} {
  const { data, loading } = useRevisionedJson<MonitoringWorkflowRoutesResponse>(
    monitoringWorkflowRoutesPath(),
    open ? "" : null
  );
  return { workflows: data?.workflows ?? [], loading };
}

export function useMonitoringWorkflowDependencies(open: boolean): {
  workflows: MonitoringWorkflowDependenciesRow[];
  loading: boolean;
} {
  const { data, loading } =
    useRevisionedJson<MonitoringWorkflowDependenciesResponse>(
      monitoringWorkflowDependenciesPath(),
      open ? "" : null
    );
  return { workflows: data?.workflows ?? [], loading };
}

// Personal-space owner names ride the personalSpaceOwners query, as spacesLite
// only relates the viewer's own grants.
export function useMonitoringSpaces(): {
  spaces: DisplayableSpace[];
  spaceById: Map<string, DisplayableSpace>;
  loading: boolean;
} {
  const [spaceRows, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const [personalSpaces] = useQuery(queries.personalSpaceOwners());
  const loading = spacesQuery.type !== "complete";

  return useMemo(() => {
    const ownerGrants = new Map(
      (personalSpaces ?? []).map((space) => [space.id, space.roleGrantsAsScope])
    );
    const spaces: DisplayableSpace[] = (spaceRows ?? []).map((space) => ({
      ...space,
      roleGrantsAsScope: ownerGrants.get(space.id) ?? [],
    }));
    return {
      spaces,
      spaceById: new Map(spaces.map((space) => [space.id, space])),
      loading,
    };
  }, [spaceRows, personalSpaces, loading]);
}
